import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import TimelineNode from 'components/TimelineNode';
import api from 'api';
import {
  BUYERS_ACTIVITY_TRACK_EVENTS,
  BUYERS_ACTIVITY_TRACK_EVENTS_COPY,
  BuyersActivityTrack,
  BuyersActivityTrackEvents,
  Lead,
} from 'models/Lead';
import TransWithNda from 'components/TransWithNda';
import { DATA_ROOM_NODE_TAG } from 'router/subrouters/DataRoom/models/DataRoomNode';
import SoftSignalIcon from 'components/SoftSignalIcon';
import DealContext from '../../../../../../providers/Deal/Deal.context';
import { MAX_LIMIT } from 'data/global';
import Nbio from '../Nbio';
import {
  checkIsUserSuperAdmin,
  getHasElementsInArray,
  getUserFullName,
  getUserFullNameOrEmail,
} from 'utils';
import uniqueId from 'lodash/uniqueId';
import useLeadNotificationCopy from 'hooks/useLeadNotificationCopy';
import NotificationMediumTag from 'components/NotificationMediumTag';
import CheckToggle from 'components/CheckToggle';
import { Trans, useTranslation } from 'react-i18next';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { LeadNotificationsModeChangedProps } from 'models/MixpanelEvents';

import './LeadTimeActivity.styles.scss';

type LeadTimeActivityProps = {
  className?: string;
  lead: Partial<Lead>;
  dealId: string;
  dealName: string;
};

export type LeadTimeActivityRef = {
  addNewActivity: (activity: Partial<BuyersActivityTrack>) => void;
};

const LeadTimeActivity: React.ForwardRefRenderFunction<
  LeadTimeActivityRef,
  LeadTimeActivityProps
> = (props, ref) => {
  const { className, lead, dealId, dealName } = props;

  const { _id: leadId } = lead || {};

  const leadName = getUserFullNameOrEmail(lead);

  const [includeNotificationEvents, setIncludeNotificationEvents] =
    useState(true);
  const [activities, setActivities] = useState<Partial<BuyersActivityTrack>[]>(
    [],
  );

  const activitiesToShow = useMemo(
    () =>
      includeNotificationEvents
        ? activities
        : activities.filter(
            ({ eventType }) =>
              eventType !== BUYERS_ACTIVITY_TRACK_EVENTS.NOTIFIED,
          ),
    [activities, includeNotificationEvents],
  );

  const { leadStagesCopies } = useContext(DealContext);

  const { t } = useTranslation();

  const getCopy = useLeadNotificationCopy();

  const onNewActivity = useCallback(
    (activity: Partial<BuyersActivityTrack>) => {
      setActivities((prevActivities) => [activity, ...prevActivities]);
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      addNewActivity: onNewActivity,
    }),
    [onNewActivity],
  );

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('lead-time-activity', className);

  const idForSubmitOffer = useMemo(
    () =>
      activities.find(
        ({ eventType }) =>
          eventType === BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER,
      )?._id,
    [activities],
  );

  const addNewActivity = useCallback(
    (eventType: BuyersActivityTrackEvents) =>
      onNewActivity({
        eventType,
        _id: uniqueId(),
        createdAt: new Date(),
      }),
    [onNewActivity],
  );

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { items },
        } = await api.portfolio.seller.getActivities(leadId, {
          $sort: '-createdAt',
          $limit: MAX_LIMIT,
        });
        setActivities(items);
      } catch {
        return;
      }
    })();
  }, [leadId]);

  if (!getHasElementsInArray(activities)) return null;

  return (
    <div className={classes}>
      <div className="include-notification-events">
        <div>
          <span>{t('includeNotificationEvents')}</span>
          <CheckToggle
            checked={includeNotificationEvents}
            onChange={({ target: { checked } }) => {
              mixpanelService.leadNotificationsModeChanged(
                getMixpanelProps({
                  leadDrawerNotificationsMode: checked ? 'on' : 'off',
                  dealId,
                  dealName,
                  leadId,
                  leadName,
                }) as LeadNotificationsModeChangedProps,
              );

              setIncludeNotificationEvents(checked);
            }}
          />
        </div>

        <span>{t('includeNotificationEventsDescription')}</span>
      </div>

      {!getHasElementsInArray(activitiesToShow) ? (
        <ReportsEmptyState />
      ) : (
        activitiesToShow.map(
          ({
            _id,
            createdAt,
            eventType,
            dataRoomNodeTag,
            oldStage,
            newStage,
            softSignal,
            dataRoomNode,
            progressedByUser,
            envelopeTemplateName,
            question,
            answer,
            withdrawnInterest,
            leadNotificationTypeCode,
            leadNotificationStatus,
            leadNotificationChannel,
          }) => (
            <TimelineNode
              key={_id}
              date={createdAt}
              text={
                <TransWithNda
                  i18nKey={BUYERS_ACTIVITY_TRACK_EVENTS_COPY[eventType]}
                  envelopeTemplateName={envelopeTemplateName}
                  isParagraph={false}
                  values={{
                    oldStage: leadStagesCopies?.[oldStage] || 'Withdraw',
                    newStage: leadStagesCopies?.[newStage] || 'Withdraw',
                    dataRoomNodeName: dataRoomNode?.name,
                    progressedByFullName: checkIsUserSuperAdmin(
                      progressedByUser?.role,
                    )
                      ? 'Resolve team'
                      : getUserFullName(
                          progressedByUser?.firstName,
                          progressedByUser?.lastName,
                        ),
                    tag: DATA_ROOM_NODE_TAG[dataRoomNodeTag],
                    leadName,
                    dealName,
                    notificationCopy: getCopy(
                      leadNotificationTypeCode,
                      leadNotificationStatus,
                      lead,
                    ),
                    withdrawnText: t(
                      withdrawnInterest
                        ? 'theyWithdrawnInterest'
                        : 'theyDidntWithdrawnInterest',
                    ),
                  }}
                  components={{
                    softSignalIcon: <SoftSignalIcon softSignal={softSignal} />,
                    medium: (
                      <NotificationMediumTag type={leadNotificationChannel} />
                    ),
                    leadQuestionLine: question ? (
                      <Trans
                        i18nKey="leadQuestionLine"
                        values={{ question }}
                        components={{ lineBreak: <br />, bold: <strong /> }}
                      />
                    ) : (
                      <></>
                    ),
                    leadAnswerLine: answer ? (
                      <Trans
                        i18nKey="leadAnswerLine"
                        values={{ answer }}
                        components={{ lineBreak: <br />, bold: <strong /> }}
                      />
                    ) : (
                      <></>
                    ),
                  }}
                />
              }
            >
              {_id === idForSubmitOffer && (
                <Nbio addNewActivity={addNewActivity} />
              )}
            </TimelineNode>
          ),
        )
      )}
    </div>
  );
};

export default forwardRef(LeadTimeActivity);
